import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RECEPTOR_CONDITIONS,
  WEEKDAY_SHORT,
  WHOLESALE_DOCUMENT_LABELS,
  WHOLESALE_METHOD_LABELS,
  WHOLESALE_PAYMENT_METHODS,
  type WholesaleAccountDTO,
  type WholesaleCustomerDTO,
  type WholesaleOrderDTO,
  type WholesaleCustomerInput,
  type WholesaleDocument,
  type WholesalePaymentMethod,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { Building2, Plus } from 'lucide-react';
import { useState } from 'react';
import { LocationPicker } from '../../components/map/LocationPicker';
import { api, errorMessage, isApiError } from '../../lib/api';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { SearchBox, Toolbar, ViewToggle } from '../../components/inventory';
import { useViewMode } from '../../lib/view-mode';
import { moneyInput, parseMoney, usePaged, usePriceLists, useStaff, useTags, useWsCustomers } from './common';
import { OrderFormDialog } from './WholesaleOrdersPage';
import './wholesale.css';
import { RemcarneCustomerFields } from './remcarne';

const IVA_LABELS: Record<(typeof RECEPTOR_CONDITIONS)[number], string> = {
  CF: 'Consumidor final',
  RI: 'Responsable inscripto',
  MONO: 'Monotributista',
  EXENTO: 'Exento',
};

type DebtFilter = '' | 'debt' | 'overdue' | 'limit' | 'credit';

/** Clientes mayoristas: ficha, lista de precios, crédito y cuenta corriente. En tarjetas o en lista, con filtros. */
export function WholesaleCustomersPage() {
  const { can } = useSession();
  const manage = can('wholesale.manage');
  const [search, setSearch] = useState('');
  const [view, setView] = useViewMode('mayorista-clientes');
  const [segment, setSegment] = useState('');
  const [zone, setZone] = useState('');
  const [seller, setSeller] = useState('');
  const [list, setList] = useState('');
  const [debt, setDebt] = useState<DebtFilter>('');
  const [editing, setEditing] = useState<WholesaleCustomerDTO | 'new' | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const q = useWsCustomers(search.trim());
  const segments = useTags('segments');
  const zones = useTags('zones');
  const staff = useStaff();
  const lists = usePriceLists();
  const rows = (q.data ?? []).filter(
    (c) =>
      (!segment || c.segmentId === segment) &&
      (!zone || c.zoneId === zone) &&
      (!seller || c.sellerUserId === seller) &&
      (!list || c.effectivePriceList?.id === list) &&
      (debt === '' ||
        (debt === 'debt' && c.balanceCents > 0) ||
        (debt === 'overdue' && c.overdueCents > 0) ||
        (debt === 'limit' && c.overLimit) ||
        (debt === 'credit' && c.balanceCents < 0)),
  );
  const filtering = !!(search || segment || zone || seller || list || debt);
  const { page, pager } = usePaged(rows, view === 'grid' ? 12 : 20, [search, segment, zone, seller, list, debt, view].join('|'));
  const totalDebt = rows.reduce((t, c) => t + Math.max(0, c.balanceCents), 0);
  const tagSelect = (label: string, value: string, set: (v: string) => void, opts: { id: string; name: string }[] | undefined) => (
    <Select value={value} onChange={(e) => set(e.target.value)} aria-label={label}>
      <option value="">{label}</option>
      {(opts ?? []).map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </Select>
  );
  return (
    <>
      <PageHeader
        title="Clientes mayoristas"
        subtitle="Negocios que te compran por mayor. Cada uno con su lista de precios, su vendedor y su cuenta corriente."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
            Nuevo cliente
          </Button>
        }
      />
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Buscar por nombre o CUIT" />
        {tagSelect('Todos los tipos', segment, setSegment, segments.data)}
        {tagSelect('Todas las zonas', zone, setZone, zones.data)}
        {manage &&
          tagSelect(
            'Todos los vendedores',
            seller,
            setSeller,
            staff.data?.filter((s) => s.canSell).map((s) => ({ id: s.userId, name: s.name })),
          )}
        {tagSelect('Todas las listas', list, setList, lists.data)}
        <Select value={debt} onChange={(e) => setDebt(e.target.value as DebtFilter)} aria-label="Cuenta corriente">
          <option value="">Cualquier saldo</option>
          <option value="debt">Con deuda</option>
          <option value="overdue">Con deuda vencida</option>
          <option value="limit">Pasados del límite</option>
          <option value="credit">Con saldo a favor</option>
        </Select>
        <span className="bf-toolbar-spacer" />
        <ViewToggle value={view} onChange={setView} />
      </Toolbar>
      {rows.length > 0 && (
        <p className="bf-cell-sub" style={{ margin: '4px 0 0' }}>
          {rows.length} {rows.length === 1 ? 'cliente' : 'clientes'} · deben en total {formatMoney(totalDebt)}
        </p>
      )}
      {q.isLoading ? (
        <Skeleton style={{ height: 200 }} />
      ) : rows.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={<Building2 />}
              title={filtering ? 'No encontramos clientes' : 'Todavía no hay clientes mayoristas'}
              text={filtering ? 'Probá con otra búsqueda o filtro.' : 'Cargá el primero con su CUIT, su zona y su tipo de negocio.'}
            />
          </CardBody>
        </Card>
      ) : view === 'grid' ? (
        <div className="bf-ws-cards">
          {page.map((c) => (
            <article key={c.id} className="bf-ws-card">
              <div className="bf-ws-card-head">
                <div>
                  <div className="bf-ws-card-name">{c.tradeName ?? c.name}</div>
                  <div className="bf-cell-sub">{[c.segmentName, c.zoneName].filter(Boolean).join(' · ') || 'Sin tipo ni zona'}</div>
                </div>
                {c.bifiPending ? <Badge tone="warning">A revisar (WhatsApp)</Badge> : c.createdBySeller && <Badge tone="info">Nuevo</Badge>}
              </div>
              <div>
                <div className="bf-cell-sub">Saldo</div>
                <div className="bf-ws-card-money" style={{ color: c.balanceCents < 0 ? 'var(--bf-success)' : undefined }}>
                  {formatMoney(c.balanceCents)}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  {c.overdueCents > 0 && <Badge tone="danger">Vencido {formatMoney(c.overdueCents)}</Badge>}
                  {c.overLimit && <Badge tone="warning">Pasado del límite</Badge>}
                </div>
              </div>
              <div className="bf-ws-card-row">
                <span>Lista</span>
                <span>{c.effectivePriceList?.name ?? 'Mostrador'}</span>
              </div>
              <div className="bf-ws-card-row">
                <span>Vendedor</span>
                <span>{c.sellerName ?? '—'}</span>
              </div>
              <div className="bf-ws-card-row">
                <span>Último pedido</span>
                <span>{c.lastOrderAt ? formatDate(c.lastOrderAt) : '—'}</span>
              </div>
              <div className="bf-ws-card-actions">
                <Button size="sm" onClick={() => setAccount(c.id)}>
                  Cuenta
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                  Editar
                </Button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Card>
          <CardBody flush>
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Tipo · zona</th>
                    <th>Lista</th>
                    <th>Vendedor</th>
                    <th>Último pedido</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {page.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="bf-cell-main">{c.tradeName ?? c.name}</div>
                        <div className="bf-cell-sub">
                          {c.tradeName ? c.name : ''} {c.cuit ? `· CUIT ${c.cuit}` : ''}
                        </div>
                      </td>
                      <td>{[c.segmentName, c.zoneName].filter(Boolean).join(' · ') || '—'}</td>
                      <td>{c.effectivePriceList?.name ?? 'Mostrador'}</td>
                      <td>{c.sellerName ?? '—'}</td>
                      <td className="bf-cell-sub">{c.lastOrderAt ? formatDate(c.lastOrderAt) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="bf-cell-main">{formatMoney(c.balanceCents)}</div>
                        {c.overdueCents > 0 && <Badge tone="danger">Vencido {formatMoney(c.overdueCents)}</Badge>}
                        {c.overLimit && <Badge tone="warning">Pasado del límite</Badge>}
                        {c.bifiPending && <Badge tone="warning">A revisar (WhatsApp)</Badge>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <Button size="sm" variant="ghost" onClick={() => setAccount(c.id)}>
                          Cuenta
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                          Editar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
      {pager}
      {editing && <CustomerDialog customer={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {account && <AccountDialog id={account} onClose={() => setAccount(null)} />}
    </>
  );
}

function CustomerDialog({ customer, onClose }: { customer: WholesaleCustomerDTO | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const manage = can('wholesale.manage');
  const lists = usePriceLists();
  const segments = useTags('segments');
  const zones = useTags('zones');
  const staff = useStaff();
  const [v, setV] = useState({
    name: customer?.name ?? '',
    tradeName: customer?.tradeName ?? '',
    cuit: customer?.cuit ?? '',
    ivaCondition: customer?.ivaCondition ?? 'CF',
    contactName: customer?.contactName ?? '',
    phone: customer?.phone ?? '',
    email: customer?.email ?? '',
    address: customer?.address ?? '',
    deliveryNotes: customer?.deliveryNotes ?? '',
    segmentId: customer?.segmentId ?? '',
    zoneId: customer?.zoneId ?? '',
    sellerUserId: customer?.sellerUserId ?? '',
    priceListId: customer?.priceListId ?? '',
    creditLimit: moneyInput(customer?.creditLimitCents),
    paymentTermDays: String(customer?.paymentTermDays ?? 0),
    defaultDocument: customer?.defaultDocument ?? '',
    notes: customer?.notes ?? '',
  });
  const [visitDays, setVisitDays] = useState<number[]>(customer?.visitDays ?? []);
  const [receiveFrom, setReceiveFrom] = useState(customer?.receiveFrom ?? '');
  const [receiveTo, setReceiveTo] = useState(customer?.receiveTo ?? '');
  const [remcarne, setRemcarne] = useState({ remcarneCategoria: customer?.remcarneCategoria ?? null, remcarneDomDestino: customer?.remcarneDomDestino ?? null });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body: WholesaleCustomerInput = {
        name: v.name,
        tradeName: v.tradeName,
        cuit: v.cuit,
        ivaCondition: v.ivaCondition as WholesaleCustomerInput['ivaCondition'],
        contactName: v.contactName,
        phone: v.phone,
        email: v.email,
        address: v.address,
        deliveryNotes: v.deliveryNotes,
        segmentId: v.segmentId || null,
        zoneId: v.zoneId || null,
        sellerUserId: v.sellerUserId || null,
        priceListId: v.priceListId || null,
        creditLimitCents: v.creditLimit.trim() ? parseMoney(v.creditLimit) : null,
        paymentTermDays: Number(v.paymentTermDays) || 0,
        defaultDocument: (v.defaultDocument || null) as WholesaleDocument | null,
        notes: v.notes,
        visitDays,
        receiveFrom,
        receiveTo,
        ...remcarne,
      };
      return customer
        ? api.put<WholesaleCustomerDTO>(`/wholesale/customers/${customer.id}`, body)
        : api.post<WholesaleCustomerDTO>('/wholesale/customers', body);
    },
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success(customer ? 'Cliente actualizado' : 'Cliente cargado', c.tradeName ?? c.name);
      onClose();
    },
    onError: (e) => {
      if (isApiError(e) && e.fields) setErrors(e.fields);
      toast.error('No se pudo guardar', errorMessage(e));
    },
  });
  const tagSelect = (label: string, k: 'segmentId' | 'zoneId', list: { id: string; name: string }[] | undefined) => (
    <Field label={label}>
      <Select value={v[k]} onChange={set(k)}>
        <option value="">Sin asignar</option>
        {(list ?? []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </Select>
    </Field>
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={customer ? `Editar ${customer.tradeName ?? customer.name}` : 'Nuevo cliente mayorista'}
      wide
    >
      <div style={{ display: 'grid', gap: 16 }}>
        <div className="bf-ws-form">
          <Field label="Razón social o nombre" error={errors.name}>
            <Input value={v.name} onChange={set('name')} autoFocus />
          </Field>
          <Field label="Nombre del local">
            <Input value={v.tradeName} onChange={set('tradeName')} />
          </Field>
          <Field label="Condición frente al IVA">
            <Select value={v.ivaCondition} onChange={set('ivaCondition')}>
              {RECEPTOR_CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {IVA_LABELS[c]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="CUIT" error={errors.cuit}>
            <Input value={v.cuit} onChange={set('cuit')} inputMode="numeric" />
          </Field>
          <Field label="Contacto">
            <Input value={v.contactName} onChange={set('contactName')} />
          </Field>
          <Field label="WhatsApp o teléfono">
            <Input value={v.phone} onChange={set('phone')} inputMode="tel" />
          </Field>
          <Field label="Email" error={errors.email}>
            <Input value={v.email} onChange={set('email')} type="email" />
          </Field>
          <Field label="Dirección de entrega">
            <Input value={v.address} onChange={set('address')} />
          </Field>
          {tagSelect('Tipo de negocio', 'segmentId', segments.data)}
          {tagSelect('Zona', 'zoneId', zones.data)}
          {manage && (
            <>
              <Field label="Vendedor">
                <Select value={v.sellerUserId} onChange={set('sellerUserId')}>
                  <option value="">Sin asignar</option>
                  {(staff.data ?? [])
                    .filter((s) => s.canSell || s.userId === v.sellerUserId)
                    .map((s) => (
                      <option key={s.userId} value={s.userId}>
                        {s.name}
                      </option>
                    ))}
                </Select>
              </Field>
              <Field label="Lista propia" hint="Si no, la de su tipo de negocio, la de su zona o la general.">
                <Select value={v.priceListId} onChange={set('priceListId')}>
                  <option value="">La que corresponda</option>
                  {(lists.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Límite de crédito" hint="Vacío = sin límite.">
                <Input value={v.creditLimit} onChange={set('creditLimit')} inputMode="decimal" />
              </Field>
            </>
          )}
          <Field label="Días para pagar">
            <Input value={v.paymentTermDays} onChange={set('paymentTermDays')} inputMode="numeric" />
          </Field>
          <Field label="Comprobante">
            <Select value={v.defaultDocument} onChange={set('defaultDocument')}>
              <option value="">El de la configuración</option>
              <option value="REMITO">{WHOLESALE_DOCUMENT_LABELS.REMITO}</option>
              <option value="INVOICE">{WHOLESALE_DOCUMENT_LABELS.INVOICE}</option>
            </Select>
          </Field>
        </div>
        <div className="bf-ws-form">
          <Field label="Recibe desde" hint="Vacío: a cualquier hora.">
            <Input type="time" value={receiveFrom} onChange={(e) => setReceiveFrom(e.target.value)} />
          </Field>
          <Field label="Recibe hasta" hint="El orden automático de la hoja de ruta lo tiene en cuenta.">
            <Input type="time" value={receiveTo} onChange={(e) => setReceiveTo(e.target.value)} />
          </Field>
        </div>
        <RemcarneCustomerFields categoria={remcarne.remcarneCategoria} domicilio={remcarne.remcarneDomDestino} onChange={setRemcarne} />
        <Field label="Días de visita" hint="Al armar la hoja de ruta de ese día se sugiere visitarlo, aunque no tenga pedido.">
          <div className="bf-ws-days" role="group" aria-label="Días de visita">
            {[1, 2, 3, 4, 5, 6, 0].map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={visitDays.includes(d)}
                onClick={() => setVisitDays(visitDays.includes(d) ? visitDays.filter((x) => x !== d) : [...visitDays, d].sort())}
              >
                {WEEKDAY_SHORT[d]}
              </button>
            ))}
          </div>
        </Field>
        {customer ? (
          <Field label="Ubicación en el mapa" hint="Sirve para ordenar las paradas de la hoja de ruta por cercanía.">
            <LocationPicker
              base={`/wholesale/customers/${customer.id}`}
              initial={{ lat: customer.lat, lng: customer.lng, source: customer.geoSource }}
              onSaved={() => void qc.invalidateQueries({ queryKey: ['wholesale', 'customers'] })}
            />
          </Field>
        ) : (
          <span className="bf-cell-sub">La ubicación en el mapa se busca sola con la dirección al guardar.</span>
        )}
        <Field label="Horario de recepción y aclaraciones para el reparto">
          <Textarea rows={2} value={v.deliveryNotes} onChange={set('deliveryNotes')} />
        </Field>
        <Field label="Notas">
          <Textarea rows={2} value={v.notes} onChange={set('notes')} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={save.isPending} disabled={v.name.trim().length < 2} onClick={() => save.mutate()}>
            Guardar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

const KIND_LABELS = { ORDER: 'Pedido', PAYMENT: 'Pago', PAYMENT_VOID: 'Pago anulado', ADJUSTMENT: 'Ajuste' } as const;

function AccountDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const manage = can('wholesale.manage');
  const q = useQuery({ queryKey: ['wholesale', 'account', id], queryFn: () => api.get<WholesaleAccountDTO>(`/wholesale/customers/${id}/account`) });
  const [mode, setMode] = useState<'pay' | 'adjust' | 'order' | null>(null);
  const [method, setMethod] = useState<WholesalePaymentMethod>('TRANSFER');
  const [amount, setAmount] = useState('');
  const [text, setText] = useState('');
  const [sign, setSign] = useState<'+' | '-'>('-');
  const refresh = () => qc.invalidateQueries({ queryKey: ['wholesale'] });
  const pay = useMutation({
    mutationFn: () =>
      api.post('/wholesale/payments', { customerId: id, stage: 'ACCOUNT', method, amountCents: parseMoney(amount) ?? 0, reference: text || null }),
    onSuccess: async () => {
      await refresh();
      toast.success('Pago registrado');
      setMode(null);
      setAmount('');
      setText('');
    },
    onError: (e) => toast.error('No se pudo registrar', errorMessage(e)),
  });
  const adjust = useMutation({
    mutationFn: () =>
      api.post(`/wholesale/customers/${id}/adjustments`, { amountCents: (sign === '-' ? -1 : 1) * (parseMoney(amount) ?? 0), description: text }),
    onSuccess: async () => {
      await refresh();
      toast.success('Ajuste cargado');
      setMode(null);
      setAmount('');
      setText('');
    },
    onError: (e) => toast.error('No se pudo cargar', errorMessage(e)),
  });
  const c = q.data?.customer;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={c ? `Cuenta de ${c.tradeName ?? c.name}` : 'Cuenta corriente'} wide>
      {!q.data || !c ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>
              Saldo: <strong>{formatMoney(c.balanceCents)}</strong> {c.balanceCents < 0 && <span className="bf-cell-sub">(a favor del cliente)</span>}
            </span>
            {c.overdueCents > 0 && <Badge tone="danger">Vencido {formatMoney(c.overdueCents)}</Badge>}
            {c.creditLimitCents !== null && <span className="bf-cell-sub">Límite {formatMoney(c.creditLimitCents)}</span>}
            <span className="bf-cell-sub">Paga a {c.paymentTermDays} días</span>
            {c.lastOrderAt && <span className="bf-cell-sub">Último pedido {formatDate(c.lastOrderAt)}</span>}
          </div>
          {manage && c.bifiPending && <ApproveBlock id={id} />}
          {manage && <UninvoicedBlock customerId={id} />}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="primary" onClick={() => setMode('order')}>
              Nuevo pedido
            </Button>
            <Button onClick={() => setMode('pay')}>Registrar pago</Button>
            {manage && <Button onClick={() => setMode('adjust')}>Ajuste</Button>}
          </div>
          {(mode === 'pay' || mode === 'adjust') && (
            <Card>
              <CardBody>
                <div className="bf-ws-form">
                  {mode === 'pay' ? (
                    <Field label="Medio">
                      <Select value={method} onChange={(e) => setMethod(e.target.value as WholesalePaymentMethod)}>
                        {WHOLESALE_PAYMENT_METHODS.map((m) => (
                          <option key={m} value={m}>
                            {WHOLESALE_METHOD_LABELS[m]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : (
                    <Field label="Tipo">
                      <Select value={sign} onChange={(e) => setSign(e.target.value as '+' | '-')}>
                        <option value="-">A favor del cliente (baja la deuda)</option>
                        <option value="+">Cargo al cliente (sube la deuda)</option>
                      </Select>
                    </Field>
                  )}
                  <Field label="Importe">
                    <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
                  </Field>
                  <Field label={mode === 'pay' ? 'Referencia (opcional)' : 'Motivo'}>
                    <Input value={text} onChange={(e) => setText(e.target.value)} />
                  </Field>
                </div>
                {mode === 'pay' && method === 'CASH' && (
                  <Callout tone="info">El efectivo entra a la caja que eligió el dueño en la configuración del Mayorista.</Callout>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                  <Button onClick={() => setMode(null)}>Cancelar</Button>
                  <Button
                    variant="primary"
                    loading={pay.isPending || adjust.isPending}
                    disabled={!parseMoney(amount) || (mode === 'adjust' && text.trim().length < 3)}
                    onClick={() => (mode === 'pay' ? pay.mutate() : adjust.mutate())}
                  >
                    Guardar
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}
          {q.data.movements.length === 0 ? (
            <p className="bf-cell-sub">Sin movimientos todavía.</p>
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Movimiento</th>
                    <th style={{ textAlign: 'right' }}>Importe</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.movements.map((m) => (
                    <tr key={m.id}>
                      <td className="bf-cell-sub">{formatDateTime(m.createdAt)}</td>
                      <td>
                        <div className="bf-cell-main">{KIND_LABELS[m.kind]}</div>
                        <div className="bf-cell-sub">{m.description}</div>
                      </td>
                      <td style={{ textAlign: 'right', color: m.amountCents < 0 ? 'var(--bf-success)' : undefined }}>{formatMoney(m.amountCents)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(m.balanceCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {mode === 'order' && <OrderFormDialog customerId={id} onClose={() => setMode(null)} onSaved={() => setMode(null)} />}
        </div>
      )}
    </Dialog>
  );
}

/** Entregas con comprobante de entrega todavía sin facturar: se facturan juntas en una sola factura. */
function UninvoicedBlock({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { me } = useSession();
  const arca = me.tenant.modules.includes('ARCA');
  const q = useQuery({
    queryKey: ['wholesale', 'uninvoiced', customerId],
    queryFn: () => api.get<WholesaleOrderDTO[]>(`/wholesale/customers/${customerId}/uninvoiced`),
  });
  const [sel, setSel] = useState<string[] | null>(null);
  const rows = q.data ?? [];
  const chosen = sel ?? rows.map((r) => r.id);
  const total = rows.filter((r) => chosen.includes(r.id)).reduce((t, r) => t + (r.totalCents ?? r.estimatedTotalCents), 0);
  const inv = useMutation({
    mutationFn: () => api.post<WholesaleOrderDTO[]>(`/wholesale/customers/${customerId}/invoice-deliveries`, { orderIds: chosen }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      setSel(null);
      toast.success('Factura hecha', 'Se está pidiendo el CAE a ARCA.');
    },
    onError: (e) => toast.error('No se pudo facturar', errorMessage(e)),
  });
  if (!rows.length) return null;
  return (
    <Callout tone="warning">
      <div style={{ display: 'grid', gap: 8 }}>
        <strong>{rows.length === 1 ? 'Una entrega sin facturar' : `${rows.length} entregas sin facturar`} (con comprobante de entrega)</strong>
        {rows.map((r) => (
          <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={chosen.includes(r.id)}
              onChange={(e) => setSel(e.target.checked ? [...chosen, r.id] : chosen.filter((x) => x !== r.id))}
            />
            Pedido #{r.number} · {formatDate(r.deliveredAt ?? r.createdAt)} · {formatMoney(r.totalCents ?? r.estimatedTotalCents)}
          </label>
        ))}
        {arca ? (
          <div>
            <Button size="sm" variant="primary" loading={inv.isPending} disabled={!chosen.length} onClick={() => inv.mutate()}>
              Facturar {chosen.length === 1 ? 'la entrega' : `las ${chosen.length} entregas`} ({formatMoney(total)})
            </Button>
          </div>
        ) : (
          <span className="bf-cell-sub">Para facturarlas desde acá hace falta el adicional de facturación electrónica (ARCA).</span>
        )}
      </div>
    </Callout>
  );
}

/** Cliente dado de alta por bifi: hasta aprobarlo no ve los precios de su lista. */
function ApproveBlock({ id }: { id: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const ok = useMutation({
    mutationFn: () => api.post(`/wholesale/customers/${id}/approve`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success('Cliente aprobado', 'Desde ahora bifi le da los precios de su lista.');
    },
    onError: (e) => toast.error('No se pudo aprobar', errorMessage(e)),
  });
  return (
    <Callout tone="warning">
      <div style={{ display: 'grid', gap: 8 }}>
        <span>
          Se dio de alta por WhatsApp con bifi y está <strong>a revisar</strong>. Revisá sus datos, asignale lista, vendedor y límite (Editar) y
          aprobalo: recién ahí bifi le da los precios de su lista.
        </span>
        <div>
          <Button size="sm" variant="primary" loading={ok.isPending} onClick={() => ok.mutate()}>
            Aprobar cliente
          </Button>
        </div>
      </div>
    </Callout>
  );
}
